import type { IResource, IResourceRecruitmentDate } from '../../interface/src/index.ts'
import { datesAreOnSameDay } from '../../common/src/index.ts'

const test = <T extends { prop: number }>(param: T) => {
  return param
}
test({ hallo: 1, prop: 1 })

type currentJobShouldContain = {
  jobs: Array<{ _id: string; startDate: string | Date; endDate?: string | Date; isAlumni: boolean }>
}

/**
 * Get the job of the moment
 * @param jobs
 * @return ICurrentJob | null
 */
export function getCurrentJob({ jobs }: any): any {
  // The job covering today, its last day included
  const getJobToday = getJobOnDay({ jobs }, new Date())

  let thisJobs = null

  if (!jobs || jobs.length === 0)
    return null

  if (getJobToday !== null)
    return getJobToday

  // Sort a copy: the caller's jobs (often reactive) must keep their order
  if (jobs && jobs.length > 0)
    thisJobs = [...jobs]

  if (thisJobs === null)
    return null

  thisJobs.sort((obj: any) => {
    if (!obj.endDate)
      return -1
    return (obj.endDate instanceof Date ? obj.endDate : new Date(obj.endDate)).getTime()
  })
  thisJobs.reverse()

  for (let k = 0; k < thisJobs.length; k++) {
    if (thisJobs[k].endDate === null || thisJobs[k].endDate === undefined)
      return thisJobs[k]
  }
  return thisJobs[0]
}

/**
 * Get the job to a given period
 * @param jobs
 * @param date
 * @return ICurrentJob | null
 */
export function getJobInPeriod<T>(
  { jobs }: any,
  date: Date,
) {
  const earliestJob = getEarliestJob({ jobs })

  for (let k = 0; k < jobs.length; k++) {
    const userStartDate = new Date(jobs[k].startDate)
    userStartDate.setHours(0, 0, 0, 0)

    if (earliestJob?._id == jobs[k]._id)
      userStartDate.setHours(0, 0, 0, 1)

    if (datesAreOnSameDay(userStartDate, date) || userStartDate < date && !jobs[k].endDate)
      return jobs[k]
    if (jobs[k].endDate && userStartDate <= date && new Date(jobs[k].endDate as string) >= date) {
      if (jobs[k].isAlumni)
        return null

      return jobs[k]
    }
    if (!jobs[k].startDate)
      return jobs[k]
  }
  return null
};

/**
 * Get the job covering a calendar day: from its start date to its end date, both days included.
 * Alumni periods record time away between two contracts and never count.
 * The job dates must be business dates converted with newDateFromUTC.
 * @param jobs
 * @param date
 * @return the job, or null outside of any contract
 */
export function getJobOnDay({ jobs }: any, date: Date) {
  const day = new Date(date)
  day.setHours(0, 0, 0, 0)

  return (jobs || []).find((job: any) => {
    if (job.isAlumni)
      return false

    const startDay = job.startDate ? new Date(job.startDate) : null
    startDay?.setHours(0, 0, 0, 0)
    const endDay = job.endDate ? new Date(job.endDate) : null
    endDay?.setHours(0, 0, 0, 0)

    return (!startDay || startDay <= day) && (!endDay || day <= endDay)
  }) ?? null
}

export const JOB_MOBILITY_TYPE = {
  PERMANENT: 'PERMANENT',
  TEMPORARY: 'TEMPORARY',
} as const

type MobilityJob = {
  _id?: unknown
  startDate?: string | Date | null
  endDate?: string | Date | null
  isAlumni?: boolean
  officeId?: unknown
  contractType?: unknown
}

export type JobMobility<T> = {
  type: typeof JOB_MOBILITY_TYPE[keyof typeof JOB_MOBILITY_TYPE]
  // A job row in the office the person comes from
  fromJob: T
}

const idOf = (value: unknown) => (value ? String(value) : null)

/** The job row ending the day before `job` starts: same person, previous contract line. */
const findPreviousJob = <T extends MobilityJob>(jobs: T[], job: T): T | null => {
  if (!job.startDate)
    return null
  const previousDay = new Date(job.startDate)
  previousDay.setDate(previousDay.getDate() - 1)

  return jobs.find(row => row !== job
    && !(row._id && idOf(row._id) === idOf(job._id))
    && !row.isAlumni
    && !!row.endDate
    && datesAreOnSameDay(new Date(row.endDate), previousDay)) ?? null
}

/**
 * Office mobility a job row is part of, read like the Headcount page
 * (`resolveMobilityType` and `resolveOfficeIdForView` in apps/api/api/dashboard/v2/hr/movements.js).
 *
 * A mobility is an office change between two consecutive job rows. It is permanent when the
 * new row comes with its own contract, temporary otherwise. A temporary mobility lasts until
 * the person goes back to their home office or signs a contract elsewhere: a promotion in the
 * host office is still on temporary mobility. A permanent mobility is read on the row that moves.
 */
export function getJobMobility<T extends MobilityJob>({ jobs }: { jobs: T[] }, job: T): JobMobility<T> | null {
  if (!jobs?.length || !job || job.isAlumni || !idOf(job.officeId))
    return null

  let current = job
  for (let guard = 0; guard < 40; guard += 1) {
    const previous = findPreviousJob(jobs, current)
    if (!previous)
      break
    const fromOfficeId = idOf(previous.officeId)
    const toOfficeId = idOf(current.officeId)
    const contractType = idOf(current.contractType)
    const isPermanentMove = !!fromOfficeId && !!toOfficeId && fromOfficeId !== toOfficeId
      && !!contractType && contractType !== idOf(previous.contractType)
    if (isPermanentMove) {
      if (current === job)
        return { type: JOB_MOBILITY_TYPE.PERMANENT, fromJob: previous }
      break
    }
    current = previous
  }

  const homeOfficeId = idOf(current.officeId)
  if (homeOfficeId && homeOfficeId !== idOf(job.officeId))
    return { type: JOB_MOBILITY_TYPE.TEMPORARY, fromJob: current }
  return null
}

export const JOB_DATE_ISSUE = {
  OVERLAP: 'OVERLAP',
  END_BEFORE_START: 'END_BEFORE_START',
  LEAVE_END_WITHOUT_START: 'LEAVE_END_WITHOUT_START',
  LEAVE_END_BEFORE_START: 'LEAVE_END_BEFORE_START',
} as const

type DatedJob = {
  _id?: unknown
  startDate?: string | Date | null
  endDate?: string | Date | null
  startDateLOA?: string | Date | null
  endDateLOA?: string | Date | null
  isAlumni?: boolean
}

export type JobDateIssue<T> = {
  type: typeof JOB_DATE_ISSUE[keyof typeof JOB_DATE_ISSUE]
  job: T
  // OVERLAP: the row that started while `job` was running (or the same day)
  otherJob?: T
  // OVERLAP: the first and last days both rows cover, no last day while both are open
  firstDay?: Date
  lastDay?: Date | null
}

const calendarDay = (value: string | Date | null | undefined) => {
  if (!value)
    return null
  const day = new Date(value)
  if (Number.isNaN(day.getTime()))
    return null
  day.setHours(0, 0, 0, 0)
  return day
}

/**
 * Job rows whose dates cannot all be true, read like the Headcount page
 * (`findJobInconsistencies` in apps/api/api/dashboard/v2/hr/movements.js): two rows covering
 * the same day (an end date is the last day worked), a row or a leave ending before it starts,
 * a leave ending without a start. Alumni periods and rows without a start date are not read.
 * Dates are local dates on the business calendar day, as the frontend gets them after newDateFromUTC.
 */
export function findJobDateIssues<T extends DatedJob>(jobs: T[]): JobDateIssue<T>[] {
  const rows = (jobs || []).filter(job => job && !job.isAlumni && calendarDay(job.startDate))
  const issues: JobDateIssue<T>[] = []
  rows.forEach((job) => {
    const end = calendarDay(job.endDate)
    if (end && end < calendarDay(job.startDate)!)
      issues.push({ type: JOB_DATE_ISSUE.END_BEFORE_START, job })
    const leaveStart = calendarDay(job.startDateLOA)
    const leaveEnd = calendarDay(job.endDateLOA)
    if (leaveEnd && !leaveStart)
      issues.push({ type: JOB_DATE_ISSUE.LEAVE_END_WITHOUT_START, job })
    if (leaveStart && leaveEnd && leaveEnd < leaveStart)
      issues.push({ type: JOB_DATE_ISSUE.LEAVE_END_BEFORE_START, job })
  })

  const periods = rows
    .map(job => ({ job, start: calendarDay(job.startDate)!, end: calendarDay(job.endDate) }))
    .filter(period => !period.end || period.end >= period.start)
    .sort((a, b) => a.start.getTime() - b.start.getTime())
  periods.forEach((period, index) => {
    periods.slice(index + 1).forEach((other) => {
      if (period.end && other.start > period.end)
        return
      const ends = [period.end, other.end].filter((end): end is Date => !!end)
      issues.push({
        type: JOB_DATE_ISSUE.OVERLAP,
        job: period.job,
        otherJob: other.job,
        firstDay: other.start,
        lastDay: ends.length ? new Date(Math.min(...ends.map(end => end.getTime()))) : null,
      })
    })
  })
  return issues
}

/** The same issue before and after a change: its type and its rows, whichever started first. */
export function jobDateIssueKey(issue: JobDateIssue<DatedJob>) {
  const ids = [issue.job, issue.otherJob].filter(Boolean).map(job => idOf(job!._id) ?? 'new').sort()
  return [issue.type, ...ids].join(':')
}

const overlapLength = (issue: JobDateIssue<DatedJob>) =>
  issue.lastDay && issue.firstDay ? issue.lastDay.getTime() - issue.firstDay.getTime() : Infinity

/**
 * The issues a change of the job rows adds: the ones `before` did not have, and the overlaps it widens.
 * The ones already there are left: a person with wrong rows is still edited, and fixed one row at a time.
 */
export function findNewJobDateIssues<T extends DatedJob>(before: T[], after: T[]): JobDateIssue<T>[] {
  const known = new Map(findJobDateIssues(before).map(issue => [jobDateIssueKey(issue), issue]))
  return findJobDateIssues(after).filter((issue) => {
    const previous = known.get(jobDateIssueKey(issue))
    if (!previous)
      return true
    return issue.type === JOB_DATE_ISSUE.OVERLAP && overlapLength(issue) > overlapLength(previous)
  })
}

/**
 * Get the first job
 * @param jobs
 * @return IResourceJob | null
 */
export function getEarliestJob({ jobs }: { jobs: Array<{ _id: string; startDate: string | Date }> }) {
  let thisJob = null
  if (!jobs?.length)
    return null
  let thisDate = new Date(jobs[0].startDate)
  thisJob = jobs[0]
  for (let i = 0; i < jobs.length; i++) {
    if (new Date(jobs[i].startDate).getTime() < thisDate.getTime()) {
      thisDate = new Date(jobs[i].startDate)
      thisJob = jobs[i]
    }
  }
  return thisJob
}

/**
 * Get the date of the recruitment
 * @param resource
 * @return Date
 */
export function getRecruitmentDate(resource: Pick<IResourceRecruitmentDate, 'recruitmentDate' | 'jobs'>): Date {
  const thisJob = resource.jobs.find((el) => {
    return el.isStartDate
  })
  if (thisJob)
    return new Date(thisJob.startDate)

  return new Date(resource.recruitmentDate)
}

// new interface interval where the start and end date are optional and year is optional too
export type IInterval = {
  startDate: Date
  endDate: Date
}

export function getResourceLOA(resource: Pick<IResource, 'jobs' | 'recruitmentDate'>): IInterval[] {
  const startDate = getRecruitmentDate(resource)
  const jobs = resource.jobs.filter(job => (job.isAlumni === false || job.startDate < startDate))
  jobs.sort((a, b) => {
    return new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
  })

  const loaList: IInterval[] = []
  jobs.forEach((job) => {
    if (job.startDateLOA && job.endDateLOA) {
      const loa = {
        startDate: new Date(job.startDateLOA),
        endDate: new Date(job.endDateLOA),
      }
      loaList.push(loa)
    }
  })

  return loaList
}

/**
 * Calculates a resource's total seniority in months. It correctly handles gaps and
 * overlaps by merging job intervals before calculating the total duration.
 *
 * @param {object} resource - The resource object with a 'jobs' array.
 * @param {Date | string | null} departureDate - The resource's departure date, if any.
 * @returns {number} The total seniority in rounded months.
 */
export const getSeniority = (resource:any, departureDate = null) => {
  if (!resource || !resource.jobs || resource.jobs.length === 0) {
    return 0;
  }
  
  const jobs = resource.jobs.filter((job:any) => !job.isAlumni)

  return jobs.reduce((acc:any, job:any) => {
    const tig = getTIG(job, departureDate)
    return acc + tig
  }, 0)

};

/**
 * Calculates the Time in Grade (TIG) for a SINGLE job in days.
 * It accounts for the job's specific start date, end date, and any Leave of Absence (LOA).
 *
 * @param {object} job - The job object with startDate, endDate, startDateLOA, endDateLOA.
 * @param {Date | string | null} customEndDate - An optional end date to cap the calculation (e.g., today or a departure date).
 * @returns {number} The duration in rounded days. Returns 0 if the job is invalid.
 */
export const getTIG = (job:any, customEndDate = null) => {
  if (!job || !job.startDate) {
    return 0; // A job must exist and have a start date.
  }

  // --- Date Preparation ---
  const startDate = new Date(job.startDate);

  // Determine the effective end date for calculation. It's the earliest of:
  // 1. The job's own end date.
  // 2. A custom end date passed in (like a global departure date).
  // 3. Today's date (if the job is ongoing and no custom end date is provided).
  const endDateCandidates = [customEndDate ? new Date(customEndDate) : new Date()];
  if (job.endDate) {
    endDateCandidates.push(new Date(job.endDate));
  }

  const effectiveEndDate = new Date(Math.min(...endDateCandidates.map(date => date.getTime())));

  if (startDate >= effectiveEndDate) {
    return 0; // Job duration is zero if it starts on or after its end date.
  }

  // --- LOA (Leave of Absence) Calculation ---
  let loaMs = 0;
  if (job.startDateLOA) {
    const startDateLOA = new Date(job.startDateLOA);
    // LOA period is also capped by the job's effective end date.
    const endDateLOA = job.endDateLOA ? new Date(job.endDateLOA) : effectiveEndDate;
    const effectiveEndDateLOA = new Date(Math.min(endDateLOA.getTime(), effectiveEndDate.getTime()));

    if (startDateLOA < effectiveEndDateLOA) {
      loaMs = effectiveEndDateLOA.getTime() - startDateLOA.getTime();
    }
  }

  // --- Final Calculation ---
  const grossDurationMs = effectiveEndDate.getTime() - startDate.getTime();
  const netDurationMs = Math.max(0, grossDurationMs - loaMs);

  // Convert net milliseconds to days
  const millisecondsInDay = 1000 * 60 * 60 * 24;
  const days = netDurationMs / millisecondsInDay;

  return Math.round(days);
};

/**
 * Finds the current job and all immediately preceding jobs with the same positionId.
 * It iterates backwards from the end of the jobs array and stops as soon as a 
 * job with a different positionId is found.
 *
 * @param {object} resource - The resource object containing a `jobs` array and a `currentJob`.
 * @returns {Array<object>} An array of job objects that are part of the current consecutive grade.
 */
export const getConsecutiveJobsForCurrentGrade = (resource: IResource) => {
  // 1. Handle edge cases where data might be missing or invalid.


  if (!resource?.currentJob?.positionId || !Array.isArray(resource.jobs) || resource.jobs.length === 0) {
    return [];
  }

  const { currentJob, jobs } = resource;
  const currentPositionId = currentJob.positionId;

  const sameJobsAsCurrentJob = jobs.filter(job => job.positionId === currentPositionId); // Simplified logic using filter for all matching jobs

  // const sameJobsAsCurrentJob = [];
  // // 2. Iterate backwards through the jobs history (which must be in chronological order).
  // for (let i = jobs.length - 1; i >= 0; i--) {
  //   const job = jobs[i];
  //   // 3. If the job's positionId matches the current one, add it to our list.
  //   if (job.positionId === currentPositionId) {
  //     // unshift() adds the item to the beginning of the array,
  //     // so the final list will be in the correct chronological order.


  //     sameJobsAsCurrentJob.unshift(job);
  //   } else {
  //     // 4. If the positionId does NOT match, the consecutive streak is broken, so we stop looking.
  //     break;
  //   }
  // }

  return sameJobsAsCurrentJob;
};
